import { planAllows, type Wall, type WallKind } from "@brandwall/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, api, post } from "../api";
import { useWorkspace } from "../components/Shell";
import { ageDays } from "../freshness";

export function Walls() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const walls = useQuery({
    queryKey: ["walls", ws],
    queryFn: () => api<{ walls: Wall[] }>(`/v1/w/${ws}/walls`),
  });
  const [error, setError] = useState<string | null>(null);

  const create = async (kind: WallKind = "normal") => {
    setError(null);
    try {
      const n = walls.data?.walls.filter((w) => w.kind === "normal").length ?? 0;
      const w = await post<Wall>(`/v1/w/${ws}/walls`, {
        name: kind === "product" ? "Product pages" : n === 0 ? "Frontpage wall" : `Wall ${n + 1}`,
        kind,
      });
      navigate(`/w/${ws}/walls/${w.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  };

  return (
    <div className="stack" style={{ gap: 16 }}>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      <div className="grid2">
        {walls.data?.walls.map((w) => {
          const age = ageDays(w.newestPostAt);
          return (
            <Link
              key={w.id}
              to={`/w/${ws}/walls/${w.id}`}
              className="card stack"
              style={{ textDecoration: "none" }}
            >
              <div className="row">
                <h2 className="spacer">{w.name}</h2>
                <span className={`pill ${w.status === "live" ? "ok" : ""}`}>
                  {w.status === "live" ? (
                    <>
                      <span className="dot" /> Live
                    </>
                  ) : (
                    "Draft"
                  )}
                </span>
              </div>
              <p className="small muted">
                {w.kind === "product"
                  ? "Product pages"
                  : w.kind === "seasonal"
                    ? "Seasonal wall"
                    : w.layout[0]!.toUpperCase() + w.layout.slice(1)}{" "}
                · {w.postCount} posts
                {age !== null ? ` · newest ${age === 0 ? "today" : `${age}d ago`}` : ""}
              </p>
              <p className="small">
                {w.pages.length
                  ? `Shown on ${w.pages.join(", ")}`
                  : w.status === "live"
                    ? "Live on your site"
                    : "Not on your site yet"}
                {w.kind !== "product" && w.postCount > 0 && (
                  <span className="muted"> · freshness {w.freshness.score}/100</span>
                )}
              </p>
              {w.hidden && w.status === "live" && (
                <p className="small">
                  <span className="pill warn">
                    Hidden: {w.hidden.have} of {w.hidden.need} posts needed
                  </span>
                </p>
              )}
            </Link>
          );
        })}
        {workspace.role !== "viewer" && (
          <div
            className="card stack"
            style={{ borderStyle: "dashed", minHeight: 100, justifyItems: "start" }}
          >
            <button type="button" className="btn" onClick={() => create("normal")}>
              + New wall
            </button>
            <button
              type="button"
              className="btn secondary"
              onClick={() => create("product")}
              disabled={!planAllows(workspace.plan, "product_walls")}
            >
              + Product-page wall
            </button>
            <p className="small muted">
              A product-page wall shows, on each product page, the posts where that product is
              tagged.
              {!planAllows(workspace.plan, "product_walls") && " On the Pro plan."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
